import { consola } from 'consola'
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { Capacitor } from '@capacitor/core'
import { useModalStore } from '@shared/model'
import { useCartStore } from '@entities/order'
import { useRoute } from '#imports'

interface PullToRefreshOptions {
  /**
   * Минимальное расстояние для активации refresh (в пикселях)
   */
  threshold?: number

  /**
   * Максимальное расстояние pull (в пикселях)
   */
  maxPullDistance?: number

  /**
   * Включить тактильную обратную связь (haptics)
   */
  hapticFeedback?: boolean

  /**
   * Селектор контейнера для отслеживания scroll
   * По умолчанию - document.documentElement
   */
  scrollContainerSelector?: string
}

/**
 * Composable для реализации Pull-to-Refresh
 * Универсальное решение для Web и Mobile (Capacitor)
 *
 * @param refreshCallback - функция которая будет вызвана при refresh
 * @param options - опции настройки
 *
 * @example
 * const { isRefreshing, pullDistance } = usePullToRefresh(async () => {
 *   await fetchData()
 * })
 */
export const usePullToRefresh = (
  refreshCallback: () => Promise<void>,
  options: PullToRefreshOptions = {},
) => {
  const {
    threshold = 80,
    maxPullDistance = 150,
    hapticFeedback = true,
    scrollContainerSelector,
  } = options

  // Состояние
  const isRefreshing = ref(false)
  const isPulling = ref(false)
  const pullDistance = ref(0)
  const canRefresh = ref(false)

  // Touch события
  let touchStartY = 0
  let touchCurrentY = 0
  let scrollContainer: HTMLElement | null = null
  let hapticTriggered = false

  // Stores для проверки состояний
  const modalStore = useModalStore()
  const cartStore = useCartStore()

  /**
   * Проверяет, может ли контейнер быть pull-to-refresh
   * (должен быть scrolled в самый верх и не должно быть открытых модальных окон или корзины)
   */
  const canPull = (): boolean => {
    // ❌ Не разрешаем pull если уже идёт обновление
    if (isRefreshing.value) return false

    // ❌ Не разрешаем pull если открыто модальное окно
    if (modalStore.isOpen) return false

    // ❌ Не разрешаем pull если открыта корзина
    if (cartStore.isBasketOpen) return false

    const container = scrollContainer || document.documentElement
    const scrollTop = container.scrollTop || window.pageYOffset || 0

    // ✅ Можем pull только если находимся в самом верху страницы
    return scrollTop === 0
  }

  /**
   * Обработчик начала touch
   */
  const handleTouchStart = (event: TouchEvent) => {
    if (!canPull()) return

    touchStartY = event.touches[0].clientY
    touchCurrentY = touchStartY
    isPulling.value = false
    canRefresh.value = false
    hapticTriggered = false
  }

  /**
   * Обработчик движения touch
   */
  const handleTouchMove = (event: TouchEvent) => {
    if (!canPull()) return

    touchCurrentY = event.touches[0].clientY
    const deltaY = touchCurrentY - touchStartY

    // Pull вниз (положительная дельта)
    if (deltaY > 0) {
      isPulling.value = true

      // Применяем resistance эффект (чем дальше тянем, тем сложнее)
      const resistance = 0.5
      const distance = Math.min(deltaY * resistance, maxPullDistance)
      pullDistance.value = distance

      // Проверяем достижение порога
      if (distance >= threshold && !canRefresh.value) {
        canRefresh.value = true
        triggerHapticFeedback()
      } else if (distance < threshold && canRefresh.value) {
        canRefresh.value = false
      }

      // Предотвращаем scroll если тянем вниз
      if (deltaY > 10) {
        event.preventDefault()
      }
    }
  }

  /**
   * Обработчик окончания touch
   */
  const handleTouchEnd = async () => {
    if (!isPulling.value) return

    isPulling.value = false

    if (canRefresh.value && !isRefreshing.value) {
      isRefreshing.value = true
      canRefresh.value = false

      try {
        await refreshCallback()
      } catch (error) {
        consola.error('[pull-to-refresh] Error during refresh:', error)
      } finally {
        // Небольшая задержка для плавной анимации
        setTimeout(() => {
          isRefreshing.value = false
          pullDistance.value = 0
        }, 300)
      }
    } else {
      // Сбрасываем без refresh
      pullDistance.value = 0
      canRefresh.value = false
    }
  }

  /**
   * Тактильная обратная связь при достижении порога
   */
  const triggerHapticFeedback = async () => {
    if (!hapticFeedback || hapticTriggered || !Capacitor.isNativePlatform()) {
      return
    }

    try {
      const { Haptics, ImpactStyle } = await import('@capacitor/haptics')
      await Haptics.impact({ style: ImpactStyle.Medium })
      hapticTriggered = true
    } catch {
      // Haptics может быть недоступен
      consola.debug('[pull-to-refresh] Haptics not available')
    }
  }

  /**
   * Ручной запуск refresh (программно)
   */
  const triggerRefresh = async () => {
    if (isRefreshing.value) return

    isRefreshing.value = true
    pullDistance.value = threshold

    try {
      await refreshCallback()
    } catch (error) {
      consola.error('[pull-to-refresh] Error during manual refresh:', error)
    } finally {
      setTimeout(() => {
        isRefreshing.value = false
        pullDistance.value = 0
      }, 300)
    }
  }

  /**
   * Отменяем pull при изменении маршрута
   */
  const route = useRoute()
  watch(() => route.path, () => {
    // Сбрасываем состояние при навигации
    if (isPulling.value || isRefreshing.value) {
      consola.debug('[pull-to-refresh] Route changed, cancelling pull')
      isPulling.value = false
      isRefreshing.value = false
      pullDistance.value = 0
      canRefresh.value = false
    }
  })

  /**
   * Инициализация
   */
  onMounted(() => {
    if (!process.client) return

    // Находим scroll container
    if (scrollContainerSelector) {
      scrollContainer = document.querySelector(scrollContainerSelector)
    }

    // Регистрируем touch listeners
    const targetElement = scrollContainer || document

    targetElement.addEventListener('touchstart', handleTouchStart, { passive: true })
    targetElement.addEventListener('touchmove', handleTouchMove, { passive: false })
    targetElement.addEventListener('touchend', handleTouchEnd, { passive: true })

    consola.debug('[pull-to-refresh] Initialized', {
      threshold,
      maxPullDistance,
      hapticFeedback,
      platform: Capacitor.getPlatform(),
    })
  })

  /**
   * Очистка
   */
  onBeforeUnmount(() => {
    if (!process.client) return

    // ✅ Сначала сбрасываем все состояния
    isRefreshing.value = false
    isPulling.value = false
    pullDistance.value = 0
    canRefresh.value = false

    const targetElement = scrollContainer || document

    // Удаляем слушатели
    targetElement.removeEventListener('touchstart', handleTouchStart)
    targetElement.removeEventListener('touchmove', handleTouchMove)
    targetElement.removeEventListener('touchend', handleTouchEnd)

    consola.debug('[pull-to-refresh] Cleanup')
  })

  return {
    isRefreshing,
    isPulling,
    pullDistance,
    canRefresh,
    triggerRefresh,
  }
}
